import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Bell, Moon, Sun } from 'react-feather';
import { Link, useLocation } from 'react-router-dom';
import { Logo } from './components/Logo';
import { CiHome } from 'react-icons/ci';
import BgImg from 'assets/bg.png';
import { UserNavigation } from './components/UserNavigation';
import { useDarkMode } from 'hooks/useDarkMode';
import { Text } from 'components/Elements';
import { MessageCircle } from 'lucide-react';
import { INTERCOM_SERVICE_URL } from 'config';

const INTERCOM_ACCESS_TOKEN = process.env.REACT_APP_INTERCOM_ACCESS_TOKEN;

const fetchUnreadMessagesCount = async () => {
  const response = await fetch(
    `${INTERCOM_SERVICE_URL}/intercom/api/conversations?state=open`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${INTERCOM_ACCESS_TOKEN}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
    }
  );

  console.log(response)
  const data = await response.json();
  return data.conversations.length || 0;
};

export const MainLayout = ({ children }) => {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    const getUnreadMessages = async () => {
      const count = await fetchUnreadMessagesCount();
      setUnreadMessages(count);
    };
    getUnreadMessages();
  }, []);

  const navLinks = [
    {
      to: 'app',
      label: (
        <div className="text-sm bg-gray-100 dark:bg-gray-700 rounded-full h-8 w-8 flex flex-row items-center justify-center">
          <CiHome />
        </div>
      ),
      isIcon: true,
    },
    { to: '/app/administration', label: 'Administration' },
    { to: '/app/finance', label: 'Finance' },
    { to: '/app/users', label: 'Users' },
    { to: '/app/transactions', label: 'Transactions' },
    { to: '/app/kyc', label: 'KYC Console' },
  ];

  return (
    <div
      className="h-screen flex flex-col transition-all dark:bg-gray-800 dark:text-white"
      style={{ backgroundImage: isDarkMode ? 'none' : `url(${BgImg})` }}
    >
      <div className="flex flex-row items-center space-x-8 justify-center py-3 mt-4">
        <Logo />
        <div className="rounded-full flex items-center space-x-10 px-12 py-3 shadow-md bg-gray-800">
          {navLinks.map(({ to, label, isIcon }) => (
            <Link key={to} to={to}>
              {isIcon ? (
                label
              ) : (
                <Text
                  variant="body"
                  className={`${
                    location.pathname === to ? 'text-[#01B9FF]' : 'text-white'
                  }`}
                >
                  {label}
                </Text>
              )}
            </Link>
          ))}
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => toggleDarkMode(!isDarkMode)}
            className="bg-gray-800 dark:bg-gray-700 shadow-lg dark:shadow-none text-white p-4 rounded-full hover:bg-gray-600 transition"
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <Link
            to="/app/conversations"
            className="bg-gray-800 shadow-lg dark:bg-gray-700 rounded-full p-4 relative"
          >
            <MessageCircle className="text-white" size={20} />
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                {unreadMessages}
              </span>
            
          </Link>
          <Link
            to="."
            className="bg-gray-800 shadow-lg dark:bg-gray-700 rounded-full p-4"
          >
            <Bell className="text-white" size={20} />
          </Link>
          <UserNavigation />
        </div>
      </div>
      <main className="flex-1 overflow-y-auto">{children}</main>
      <footer className="flex flex-col items-center justify-center py-4 text-xs">
        <Text variant="caption">
          © {currentYear}, Wire24. All rights reserved.
        </Text>
        <div className="flex space-x-2">
          {[
            'terms-and-conditions',
            'privacy-policy',
            'cookie-policy',
            'legal',
          ].map((path, idx) => (
            <React.Fragment key={path}>
              {idx > 0 && <span>|</span>}
              <Link to={`/${path}`} className="hover:underline">
                <Text variant="caption">{path.replace('-', ' ')}</Text>
              </Link>
            </React.Fragment>
          ))}
        </div>
      </footer>
    </div>
  );
};

MainLayout.propTypes = {
  children: PropTypes.node.isRequired,
};
